import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Las piezas de estructura del estándar de estilos — `T-42`.
 *
 * Cuatro cosas que el estándar describe, que se repiten en toda pantalla de
 * administración y que hasta hoy cada una escribía a mano: la cabecera, la
 * tabla, el icono de ayuda y la tarjeta de sección de formulario.
 *
 * > [!info] Los tamaños salen de la escala, no del estándar
 * > El estándar da números —título 28-30, celda 13, pastilla 11— y la escala de
 * > `tokens.css` ya tiene los suyos. Isaac decidió que **manda la escala**, así
 * > que lo que se toma del estándar es la ESTRUCTURA y el PESO, y el tamaño sale
 * > del token más cercano. Coinciden en casi todo —11, 13, 15, 30 están los
 * > cuatro— y donde no, el token gana: radio de botón 12 y no 8, descripción de
 * > aviso 13 y no 13.5.
 */
import { useId } from 'react';
/* ── Cabecera de pantalla ──────────────────────────────────────────────────── */
/**
 * Rótulo → título → una línea. **Siempre esas tres piezas, en ese orden.**
 *
 * El tope de 640 px del subtítulo no es decorativo y es lo único de aquí que
 * hace trabajo por sí solo: obliga a que sea **una línea y no un párrafo**. Sin
 * él, el sitio donde se explica la pantalla se convierte en el sitio donde se
 * cuenta todo lo que no cupo en otro lado, y deja de leerse.
 *
 * El punto final del título —«Extras.»— es el gesto de marca del estándar. Se
 * copia o no se copia, pero se hace igual en todas: media docena de títulos con
 * punto y media sin él no se lee como una decisión, se lee como un descuido.
 */
export const CabeceraDePantalla = ({ rotulo, titulo, children }) => (_jsxs("header", { className: "fr-cabecera", children: [rotulo === undefined ? null : _jsx("span", { className: "fr-cabecera__rotulo", children: rotulo }), _jsx("h1", { className: "fr-cabecera__titulo", children: titulo }), children === undefined ? null : _jsx("p", { className: "fr-cabecera__linea", children: children })] }));
/* ── Ayuda de campo ───────────────────────────────────────────────────────── */
/**
 * El icono de información junto a la etiqueta de un campo.
 *
 * > [!warning] Sólo donde de verdad haya duda
 * > Lo dice el estándar y es la mitad importante: si todos los campos lo llevan,
 * > deja de significar «aquí hay algo que saber» y pasa a ser decoración de
 * > formulario. En esta pantalla hay exactamente dos.
 *
 * ── Con teclado, no sólo con el ratón ─────────────────────────────────────
 * Es un `<button>` y no un `<span title>` por eso: `title` no existe para quien
 * navega con tabulador ni para quien usa una pantalla táctil, que entre las dos
 * son la mayoría. El texto vive en el árbol, atado con `aria-describedby`, y se
 * enseña al posarse **y al enfocar**.
 */
export const AyudaDeCampo = ({ de, children }) => {
    const id = useId();
    return (_jsxs("span", { className: "fr-ayuda", children: [_jsx("button", { type: "button", className: "fr-ayuda__boton", "aria-label": `Qué es ${de}`, "aria-describedby": id, children: _jsxs("svg", { width: "14", height: "14", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: [_jsx("circle", { cx: "8", cy: "8", r: "6.2" }), _jsx("path", { d: "M6.4 6.2a1.6 1.6 0 1 1 2.2 1.5c-.4.2-.6.5-.6.9v.3M8 11.6h.01" })] }) }), _jsx("span", { className: "fr-ayuda__globo", id: id, role: "tooltip", children: children })] }));
};
/* ── Tarjeta de sección de formulario ─────────────────────────────────────── */
/**
 * Icono en círculo teñido, título, y una línea que dice **para qué sirve la
 * sección**.
 *
 * Esa línea es el trabajo de la tarjeta. Un formulario largo partido en cajas
 * sin explicar se lee igual de largo; partido y con el porqué de cada trozo se
 * lee como tres decisiones pequeñas. Sin sombra y con filete, que es la regla de
 * la casa para todo lo que no flota.
 */
export const SeccionDeFormulario = ({ icono, titulo, para, children }) => (_jsxs("section", { className: "fr-seccion-form", children: [_jsxs("div", { className: "fr-seccion-form__cabecera", children: [_jsx("span", { className: "fr-seccion-form__icono", "aria-hidden": "true", children: icono }), _jsxs("div", { className: "fr-seccion-form__identidad", children: [_jsx("h3", { className: "fr-seccion-form__titulo", children: titulo }), _jsx("p", { className: "fr-seccion-form__para", children: para })] })] }), children] }));
//# sourceMappingURL=estructura.js.map